(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.BDTFArtwork = api;
})(typeof window === 'object' ? window : this, function () {
  const BACKGROUNDS = Object.freeze({ light: '#FFFFFF', medium: '#777777', dark: '#252525' });
  // Presentation only: the saved marker still describes the contrast family.
  // Both checker cells stay in that family so alpha remains visible.
  const CHECKERS = Object.freeze({ light: ['#FFFFFF', '#E5E5E5'], medium: ['#777777', '#909090'], dark: ['#252525', '#414141'] });
  const linear = value => value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
  const luminance = (r, g, b) => .2126 * linear(r / 255) + .7152 * linear(g / 255) + .0722 * linear(b / 255);
  const ratio = (a, b) => (Math.max(a, b) + .05) / (Math.min(a, b) + .05);
  const VERSION = 3;
  function analyze(rgba, dimensions = {}) {
    if (!rgba || rgba.length % 4) throw new Error('Artwork analysis requires RGBA pixels.');
    const histogram = new Float64Array(256);
    const width = dimensions.width, height = dimensions.height;
    const spatial = Number.isInteger(width) && Number.isInteger(height) && width * height * 4 === rgba.length;
    let weight = 0, scoreWeight = 0, total = 0, samples = 0;
    let paleWeight = 0, boundaryWeight = 0, paleBoundary = 0, darkBoundary = 0;
    const stride = Math.max(1, Math.ceil(rgba.length / 4 / 16384)) * 4;
    for (let i = 0; i < rgba.length; i += stride) {
      samples++;
      const alpha = rgba[i + 3] / 255;
      if (alpha < .12) continue; // Transparent pixels do not vote for a background.
      const w = alpha * alpha, value = luminance(rgba[i], rgba[i + 1], rgba[i + 2]);
      const pixel = i / 4, x = pixel % width, y = Math.floor(pixel / width);
      // Contrast against the background matters most at the transparent edge.
      // Filled interior areas already contrast against their own outlines.
      const edge = spatial && (x === 0 || y === 0 || x === width - 1 || y === height - 1
        || [i - 4, i + 4, i - width * 4, i + width * 4].some(j => rgba[j + 3] < 64));
      if (value >= .78) paleWeight += w;
      if (edge || !spatial) {
        boundaryWeight += w;
        if (value >= .78) paleBoundary += w;
        if (value < .1) darkBoundary += w;
      }
      const vote = w * (edge ? 12 : 1);
      histogram[Math.round(value * 255)] += vote; scoreWeight += vote; weight += w; total += value * w;
    }
    if (!weight) return { version: VERSION, kind: 'light', color: BACKGROUNDS.light, averageLuminance: null, visibleCoverage: 0 };
    const scores = {};
    for (const [kind, color] of Object.entries(BACKGROUNDS)) {
      const bg = luminance(parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16));
      let loss = 0;
      for (let i = 0; i < 256; i++) if (histogram[i]) {
        const contrast = ratio(i / 255, bg);
        loss += histogram[i] * ((contrast < 1.8 ? .7 : 0) + .3 / contrast);
      }
      scores[kind] = loss / scoreWeight;
    }
    // White is the default, not a competitor in a maximum-contrast contest.
    // Only substantial, almost-white artwork exposed to transparency needs
    // a darker surface. Cream fills inside dark outlines and saturated colors
    // remain readable on the light checker, even if dark scores higher.
    const paleFraction = paleWeight / weight;
    const paleEdgeFraction = boundaryWeight ? paleBoundary / boundaryWeight : 0;
    const darkEdgeFraction = boundaryWeight ? darkBoundary / boundaryWeight : 0;
    const losesArtwork = (paleEdgeFraction >= .3 && paleFraction >= .12)
      || (paleEdgeFraction >= .65 && paleFraction >= .04);
    let kind = 'light';
    if (losesArtwork && scores.light - Math.min(scores.medium, scores.dark) >= .08) {
      // Reserve the darkest checker for overwhelmingly pale exposed edges.
      // A middle checker preserves mixed black/white lettering and contours.
      kind = paleEdgeFraction >= .65 && darkEdgeFraction < .12 ? 'dark' : 'medium';
    }
    return { version: VERSION, kind, color: BACKGROUNDS[kind], averageLuminance: Math.round(total / weight * 10000) / 10000,
      visibleCoverage: Math.round(weight / samples * 10000) / 10000,
      paleFraction: Math.round(paleFraction * 10000) / 10000,
      paleEdgeFraction: Math.round(paleEdgeFraction * 10000) / 10000 };
  }
  function kind(marker) { return Object.hasOwn(BACKGROUNDS, marker?.kind || marker) ? marker?.kind || marker : 'light'; }
  function apply(element, marker, { checker = true } = {}) {
    if (!element) return;
    const selected = kind(marker);
    element.dataset.artworkBackground = selected;
    element.style.backgroundColor = BACKGROUNDS[selected];
    const [first, second] = CHECKERS[selected];
    element.style.backgroundImage = checker ? `repeating-conic-gradient(${first} 0% 25%, ${second} 0% 50%)` : 'none';
    element.style.backgroundSize = checker ? '20px 20px' : 'auto';
  }
  return { VERSION, BACKGROUNDS, CHECKERS, analyze, kind, apply };
});
